import React from 'react';
import PropTypes from 'prop-types';
import Button from '../button';
import { DeletIcon } from '../../utils/svg';

const PreviewModal = ({
  previewModalOpen,
  setPreviewModalOpen,
  stagedFiles,
  setStagedFiles,
}) => {
  if (!previewModalOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPreviewModalOpen(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-800">Files Preview</h3>
            <p className="text-xs text-gray-400 mt-0.5">{stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} ready to upload</p>
          </div>
          <Button variant='text' onClick={() => setPreviewModalOpen(false)}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        {/* Content */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {stagedFiles.length === 1 ? (
            // Single file — full preview
            <div className="space-y-3">
              <div className="w-full rounded-xl overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center" style={{ minHeight: 240 }}>
                {stagedFiles[0].previewUrl ? (
                  <img src={stagedFiles[0].previewUrl} alt="preview" className="w-full max-h-72 object-contain" />
                ) : (
                  <div className="flex flex-col items-center gap-2 py-12">
                    <svg className="w-14 h-14 text-orange-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-bold text-orange-400">{stagedFiles[0].file.name.split('.').pop().toUpperCase()}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between px-1">
                <div>
                  <p className="text-sm font-medium text-gray-700">{stagedFiles[0].file.name}</p>
                  <p className="text-xs text-gray-400">{(stagedFiles[0].file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => {
                    setStagedFiles([]);
                    setPreviewModalOpen(false);
                  }}
                >
                  <DeletIcon color='red' />
                </Button>
              </div>
            </div>
          ) : (
            // Multiple files — grid
            <div className="grid grid-cols-2 gap-3">
              {stagedFiles.map((staged) => (
                <div key={staged.id} className="group relative rounded-xl border border-gray-100 overflow-hidden bg-gray-50 hover:border-orange-200 transition-all">
                  <div className="w-full h-36 flex items-center justify-center bg-white">
                    {staged.previewUrl ? (
                      <img src={staged.previewUrl} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5">
                        <svg className="w-10 h-10 text-orange-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-bold text-orange-400">{staged.file.name.split('.').pop().toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="px-2.5 py-2">
                    <p className="text-xs font-medium text-gray-600 truncate">{staged.file.name}</p>
                    <p className="text-[10px] text-gray-400">{(staged.file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button
                    variant='text'
                    size='small'
                    onClick={() => {
                      setStagedFiles((prev) => prev.filter((s) => s.id !== staged.id));
                      if (stagedFiles.length === 1) setPreviewModalOpen(false);
                    }}
                  >
                    <DeletIcon color='red' />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center bg-gray-50">
          <Button
            variant='text'
            onClick={() => {
              setStagedFiles([]);
              setPreviewModalOpen(false);
            }}
            containerClass="!text-red-500 hover:!text-red-700 !text-xs !font-medium underline underline-offset-2"
          >
            Remove All
          </Button>
          <Button onClick={() => setPreviewModalOpen(false)} size='small'>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

PreviewModal.propTypes = {
  previewModalOpen: PropTypes.bool.isRequired,
  setPreviewModalOpen: PropTypes.func.isRequired,
  stagedFiles: PropTypes.array.isRequired,
  setStagedFiles: PropTypes.func.isRequired,
};

export default PreviewModal;
